import { useMemo, useState } from 'react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react';
import { RangeCalendar } from '@nextui-org/calendar';
import { Icon } from '@iconify/react';
import { parseDate } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';
import { I18nProvider } from '@react-aria/i18n';
import { formatDateLocale, getUserLocaleAndTimezone } from '../utils/dateutils';

type DateRangeInputProps = {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  minDate?: string;
  ariaLabel?: string;
  className?: string;
  placeholder?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

function toCalendarRange(startDate: string, endDate: string): RangeValue<DateValue> | null {
  try {
    const start = startDate ? parseDate(startDate) : null;
    const end = endDate ? parseDate(endDate) : null;
    if (start && end) {
      return { start, end };
    }
    if (start) {
      return { start, end: start };
    }
    return null;
  } catch {
    return null;
  }
}

function formatRangeLabel(startDate: string, endDate: string, placeholder: string): string {
  if (startDate && endDate) {
    return `${formatDateLocale(startDate)} – ${formatDateLocale(endDate)}`;
  }
  if (startDate) {
    return `${formatDateLocale(startDate)} – …`;
  }
  return placeholder;
}

export function DateRangeInput({
  startDate,
  endDate,
  onChange,
  minDate,
  ariaLabel = 'Date range',
  className = '',
  placeholder = 'Select date range',
  isOpen: isOpenControlled,
  onOpenChange,
}: Readonly<DateRangeInputProps>) {
  const [isOpenUncontrolled, setIsOpenUncontrolled] = useState(false);
  const isOpen = isOpenControlled ?? isOpenUncontrolled;
  const setIsOpen = (open: boolean) => {
    onOpenChange?.(open);
    if (isOpenControlled === undefined) {
      setIsOpenUncontrolled(open);
    }
  };
  const { locale } = getUserLocaleAndTimezone();
  const calendarValue = useMemo(() => toCalendarRange(startDate, endDate), [startDate, endDate]);
  const label = formatRangeLabel(startDate, endDate, placeholder);
  const minValue = useMemo(() => {
    if (!minDate) {
      return undefined;
    }
    try {
      return parseDate(minDate);
    } catch {
      return undefined;
    }
  }, [minDate]);

  return (
    <I18nProvider locale={locale}>
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom-start"
        offset={4}
        shouldBlockScroll={false}
        classNames={{ content: 'z-[9999] p-0' }}
      >
        <PopoverTrigger>
          <Button
            variant="bordered"
            size="sm"
            aria-label={ariaLabel}
            className={`h-9 min-w-[200px] flex-1 justify-start border-default-200 bg-transparent font-normal ${className}`}
            startContent={<Icon icon="mdi:calendar" className="h-4 w-4 shrink-0 text-default-400" />}
          >
            <span
              className={`truncate text-sm ${startDate || endDate ? 'text-foreground' : 'text-default-400'}`}
            >
              {label}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent aria-label={`${ariaLabel} picker`} className="w-auto max-w-none p-0">
          <div className="rounded-medium bg-content1 p-1 overflow-x-auto">
            <RangeCalendar
              aria-label={ariaLabel}
              value={calendarValue}
              minValue={minValue}
              visibleMonths={2}
              onChange={(nextRange) => {
                if (!nextRange?.start) {
                  onChange('', '');
                  return;
                }
                const nextStart = nextRange.start.toString();
                const nextEnd = nextRange.end?.toString() ?? nextStart;
                onChange(nextStart, nextEnd);
                if (nextRange.start && nextRange.end) {
                  setIsOpen(false);
                }
              }}
            />
          </div>
        </PopoverContent>
      </Popover>
    </I18nProvider>
  );
}
